import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import SogPage from "@/shared/ui/SogPage.vue";
import {onMounted, ref, watch} from "vue";
import { onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute } from "vue-router";
import {useSpinner} from "@/shared/composables/SogSpinner";
import apiStore from "@/modules/Store/api";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import StoreExportBlankItem from "@/shared/model/StoreExportBlankItem";
import SogGrid from "@/shared/ui/SogGrid.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import type {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {DefaultColumn, NumberColumn } from "@/shared/ui/types/SogGridColumnDefinitions";
import { useRouter } from "vue-router";
import {useShoppingCartStore} from "@/modules/ShoppingCart/store";

interface Props {
  id?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StoreExportBlankPage',
  props: {
    id: { default: undefined }
  },
  setup(__props: any) {

const props = __props

const _title = ref("Template");
const _spinner = useSpinner();
const _grid = ref<InstanceType<typeof SogGrid> | null>(null);
const _data = ref<StoreExportBlankItem[]>([] as StoreExportBlankItem[]);
const _isFilialeVisible = ref<boolean>(false);
const _selectedItem = ref<StoreExportBlankItem | undefined>(undefined);
const router = useRouter();
const _shoppingCartStore = useShoppingCartStore();

const _columns = ref<ColumnModel[]>([
  {
    ...DefaultColumn,
    field: "personName",
    headerText: "Person Name"
  },
  {
    ...DefaultColumn,
    field: "blankCount",
    headerText: "Blank Count"
  },
  {
    ...DefaultColumn,
    field: "exportBlankMinimumCount",
    headerText: "Min"
  },
  {
    ...DefaultColumn,
    field: "exportBlankMaximumCount",
    headerText: "Max"
  },
  {
    ...DefaultColumn,
    field: "exportBlankCountToSend",
    headerText: "to sending"
  },
  {
    ...NumberColumn,
    field: "orderedCount7",
    headerText: "Ordered Count 7"
  },
  {
    ...NumberColumn,
    field: "orderedCount30",
    headerText: "Ordered Count 30"
  },
  {
    ...NumberColumn,
    field: "averagePerWeek",
    headerText: "Average Per Week",
    format: "N2"
  },
  {
    ...NumberColumn,
    field: "averagePerMonth",
    headerText: "Average Per Month",
    format: "N2"
  },
  {
    ...NumberColumn,
    field: "averagePerSaleMonth",
    headerText: "Average Per Sale Month",
    format: "N2"
  }
]);

async function p_init(route: RouteLocationNormalizedLoaded) {
  _spinner.show()
  try {
    _title.value = "Ausfuhrformulare"
    await p_refresh()
  }
  finally {
    _spinner.hide()
  }
}

async function p_refresh(personId?: string) {
  _spinner.show()
  try {
    const t_idToUse = personId ?? props.id;

    const t_res = await apiStore.storeExportBlank(t_idToUse)
    _data.value = [...t_res].sort((a, b) =>
        (a.personName ?? "").localeCompare(b.personName ?? "")
    )
  }
  finally {
    _spinner.hide()
  }
}

function p_onRowSelected(args:any):void {
  const t_item = args?.data as StoreExportBlankItem | undefined;

  if (t_item?.personId) {
    _isFilialeVisible.value = true;
    _selectedItem.value = t_item
  }
}

function p_onRowDeselected():void {
  _isFilialeVisible.value = false;
  _selectedItem.value = undefined;
}

async function p_onShowFiliale(): Promise<void> {
  if(!_selectedItem.value?.personId) return;

  await router.push({
    name: "store-exportblank",
    params: { id: _selectedItem.value.personId }
  });
}

/**
 * Startet eine Streckenlieferung: verkauft wird an den Kunden, dessen Filialen diese
 * Seite zeigt, geliefert wird an die markierte Filiale. Der Verkaufsassistent
 * übernimmt Person, Produkt, Preis und Stückzahl.
 */
async function p_onFormularBestellungClick(): Promise<void> {
  const t_branchId = _selectedItem.value?.personId;
  // props.id ist der Kunde, dessen Filialen hier stehen; ohne ihn ist es der
  // eingeloggte Nutzer selbst - dann gibt es keine Streckenlieferung.
  const t_customerId = props.id;

  if (!t_branchId || !t_customerId) return;

  _spinner.show()
  try {
    await _shoppingCartStore.initExportBlankDelivery(
        t_customerId,
        t_branchId,
        _selectedItem.value?.exportBlankCountToSend ?? 0
    )
  }
  finally {
    _spinner.hide()
  }
}

watch(
    () => props.id,
    async (t_newId) => {
      await p_refresh(t_newId);
    },
    { immediate: true }
);


onMounted(async () => {
  const route = useRoute();
  await p_init(route)
})

onBeforeRouteUpdate(async (to) => {
  await p_init(to)
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SogPage, {
    id: "page",
    title: _title.value
  }, {
    default: _withCtx(() => [
      _createVNode(SogCard, null, {
        default: _withCtx(() => [
          _createVNode(SogCardActions, null, {
            default: _withCtx(() => [
              _createVNode(SogCardActionButton, {
                kind: _unref(SogButtonKind).Refresh,
                onClick: p_refresh
              }, null, 8, ["kind"]),
              _withDirectives(_createVNode(SogCardActionButton, {
                "mdi-icon": 'mdi-office-building',
                tooltip: "Filiale anzeigen",
                onClick: p_onShowFiliale
              }, null, 512), [
                [_vShow, _isFilialeVisible.value]
              ]),
              _withDirectives(_createVNode(SogCardActionButton, {
                "mdi-icon": 'mdi-cart-outline',
                tooltip: "Ausfuhrformular bestellen",
                onClick: p_onFormularBestellungClick
              }, null, 512), [
                [_vShow, _isFilialeVisible.value]
              ]),
              _createVNode(SogCardActionButton, {
                kind: _unref(SogButtonKind).Back,
                style: {"align-self":"flex-end"}
              }, null, 8, ["kind"])
            ]),
            _: 1
          }),
          _createVNode(SogCardContent, null, {
            default: _withCtx(() => [
              _createVNode(SogGrid, {
                ref_key: "_grid",
                ref: _grid,
                data: _data.value,
                columns: _columns.value,
                "allow-paging": true,
                "allow-sorting": true,
                "hide-scroll": true,
                onRowSelected: p_onRowSelected,
                onRowDeselected: p_onRowDeselected
              }, null, 8, ["data", "columns"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})